/**
 * Minimum viable payment safety screen (Task 7b).
 * Renders one obligation row per card. Non-active grace → do not spend + reason.
 */

import type { ReactNode } from 'react';
import { formatCentsDisplay } from '../../domain/recommend/explain';
import type { Cents } from '../../domain/money';
import type { PaymentSafetyCardView, RecurringChargeSafetyView, SafetyField } from './types';

function ProvenanceBadge({ field }: { field: SafetyField<unknown> }): JSX.Element {
  if (field.provenance === 'observed') {
    return (
      <span className="text-xs text-slate-500">observed · {field.observed_at.slice(0, 10)}</span>
    );
  }
  return <span className="text-xs text-slate-500">computed</span>;
}

function formatMoney(cents: Cents | null): string {
  if (cents === null) return '—';
  return formatCentsDisplay(cents);
}

function FieldRow(props: {
  label: string;
  field: SafetyField<unknown>;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5 border-t border-slate-800 py-2 first:border-t-0">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs uppercase tracking-wide text-slate-500">{props.label}</span>
        <ProvenanceBadge field={props.field} />
      </div>
      <div className="text-sm text-slate-100">{props.children}</div>
    </div>
  );
}

function ObligationRow({ card }: { card: PaymentSafetyCardView }): JSX.Element {
  const autopay = card.autopay.value;
  const autopayText =
    autopay.enabled === null
      ? 'Unknown'
      : autopay.enabled === false
        ? 'Off'
        : `On · ${autopay.kind}${
            autopay.amount_cents !== null ? ` · ${formatMoney(autopay.amount_cents)}` : ''
          }`;

  const lastPay = card.last_payment_posted.value;

  return (
    <article
      data-testid="obligation-row"
      data-card-id={card.card_id}
      className="rounded-lg border border-slate-800 bg-slate-900/60 p-4"
    >
      <header className="mb-2 flex items-start justify-between gap-3">
        <h3 className="text-base font-medium text-slate-100">{card.label}</h3>
        {card.spend_allowed ? (
          <span className="rounded border border-emerald-800 px-2 py-0.5 text-xs text-emerald-300">
            Spend eligible
          </span>
        ) : (
          <span className="rounded border border-rose-800 px-2 py-0.5 text-xs font-medium text-rose-300">
            Do not spend
          </span>
        )}
      </header>

      {!card.spend_allowed ? (
        <p className="mb-3 text-sm text-rose-200/90">{card.grace_reason.value}</p>
      ) : null}

      <FieldRow label="Statement balance" field={card.statement_balance}>
        {formatMoney(card.statement_balance.value)}
      </FieldRow>
      <FieldRow label="Due date" field={card.payment_due_date}>
        {card.payment_due_date.value ?? '—'}
      </FieldRow>
      <FieldRow label="Minimum due" field={card.minimum_due}>
        {formatMoney(card.minimum_due.value)}
      </FieldRow>
      <FieldRow label="Autopay" field={card.autopay}>
        {autopayText}
      </FieldRow>
      <FieldRow label="Issued-statement obligation (due now)" field={card.due_now}>
        {formatMoney(card.due_now.value)}
        {card.next_due_date.value !== null ? ` by ${card.next_due_date.value}` : ''}
      </FieldRow>
      <FieldRow label="Cash reserved" field={card.cash_reserved}>
        {formatMoney(card.cash_reserved.value)}
      </FieldRow>
      <FieldRow label="Grace status" field={card.grace_status}>
        {card.grace_status.value}
      </FieldRow>
      <FieldRow label="Grace reason" field={card.grace_reason}>
        {card.grace_reason.value}
      </FieldRow>
      <FieldRow label="Payment posted confirmation" field={card.last_payment_posted}>
        {lastPay === null
          ? 'No posted payment on record'
          : `${formatMoney(lastPay.amount_cents)} posted ${lastPay.posted_date}`}
      </FieldRow>
    </article>
  );
}

export type PaymentSafetyScreenProps = {
  cards: readonly PaymentSafetyCardView[];
  /** Flagged recurring charges — expected upcoming spend (Task 21). */
  recurringCharges?: readonly RecurringChargeSafetyView[];
};

export function PaymentSafetyScreen({
  cards,
  recurringCharges = [],
}: PaymentSafetyScreenProps): JSX.Element {
  return (
    <section data-testid="payment-safety-screen" className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-100">Payment safety</h2>
        <p className="mt-1 text-sm text-slate-400">
          Contractual obligation is due now by the statement due date. Every figure is labelled
          observed or computed — nothing is shown as authoritative unless the domain derived it.
        </p>
      </div>

      {cards.length === 0 ? (
        <p className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-400">
          No cards in the portfolio yet.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {cards.map((card) => (
            <ObligationRow key={card.card_id} card={card} />
          ))}
        </div>
      )}

      {recurringCharges.length > 0 ? (
        <div data-testid="recurring-upcoming-spend" className="flex flex-col gap-2">
          <h3 className="text-sm font-medium text-slate-200">Expected upcoming spend</h3>
          <p className="text-xs text-slate-500">
            Recurring charges you flagged — not contractual obligations.
          </p>
          {recurringCharges.map((r) => (
            <div
              key={r.id}
              data-testid="recurring-charge-row"
              data-recurring-id={r.id}
              className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm text-slate-100">{r.merchant_name}</span>
                <span className="text-sm text-slate-100">
                  {formatMoney(r.expected_amount_cents)}
                </span>
              </div>
              <p className="text-xs text-slate-500">
                {r.frequency}
                {r.next_expected_date !== null ? ` · next ${r.next_expected_date}` : ''}
                {r.assigned_card_label !== null ? ` · ${r.assigned_card_label}` : ''}
              </p>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
